import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'components';
import AntButton from 'components/AntButton/AntButton';
import { useUserApi } from 'hooks';
import styles from './WorkCodesEditor.module.css';

export const parseWorkCodes = (text, primary) => {
	const codes = text.split(/\r?\n/).map(code => code.trim()).filter(Boolean);
	if (codes.length > 20 || codes.some(code => code.length > 64 || /[\s\x00-\x1f\x7f<>]/.test(code)))
		throw new Error('Ingrese hasta 20 códigos, uno por línea, sin espacios internos y con hasta 64 caracteres.');
	if (new Set(codes).size !== codes.length || codes.includes(primary))
		throw new Error('No repita códigos ni incluya el código principal.');
	return codes;
};

export default function WorkCodesEditor({ userId }) {
	const { getWorkCodes, setWorkCodes } = useUserApi();
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [snapshot, setSnapshot] = useState(null);
	const [text, setText] = useState('');
	const request = useRef(0);
	useEffect(() => {
		request.current += 1; setVisible(false); setSnapshot(null); setLoading(false); setSaving(false); setError('');
		return () => { request.current += 1; };
	}, [userId]);

	const load = async () => {
		const generation = ++request.current;
		setVisible(true); setLoading(true); setError(''); setSnapshot(null);
		try {
			const result = await getWorkCodes(userId);
			if (generation !== request.current) return;
			setSnapshot(result); setText(result.workCodes.join('\n'));
		} catch (err) {
			if (generation === request.current) setError(err.message || 'No se pudieron cargar los códigos.');
		} finally { if (generation === request.current) setLoading(false); }
	};
	const close = () => {
		if (saving) return;
		request.current += 1; setVisible(false); setSnapshot(null);
	};
	const save = async () => {
		if (!snapshot || saving || loading) return;
		let codes;
		try { codes = parseWorkCodes(text, snapshot.workCode); }
		catch (err) { setError(err.message); return; }
		const generation = request.current;
		setSaving(true); setError('');
		try {
			await setWorkCodes({ userId, workCodes: codes, expectedWorkCodes: snapshot.workCodes });
			if (generation === request.current) { setVisible(false); setSnapshot(null); }
		} catch (err) {
			if (generation === request.current) setError(err.message || 'No se pudieron guardar los códigos.');
		} finally { if (generation === request.current) setSaving(false); }
	};

	return <>
		<button type='button' className={styles.trigger} onClick={load} aria-label={`Gestionar códigos adicionales del usuario ${userId}`}>
			Otros códigos
		</button>
		<Modal visible={visible} title='Códigos de trabajo adicionales' onCancel={close} footer={null} destroyOnClose>
			<div className={styles.content}>
				{loading ? <p role='status'>Cargando códigos…</p> : <>
					{snapshot && <>
						<p>Código principal: <strong>{snapshot.workCode || 'Sin código principal'}</strong></p>
						<label htmlFor={`additional-codes-${userId}`}>Códigos adicionales, uno por línea</label>
						<textarea id={`additional-codes-${userId}`} value={text} onChange={event => setText(event.target.value)} rows={5} disabled={saving} autoFocus />
						<p className={styles.help}>Se conservan el código principal y los permisos del técnico en cada proyecto.</p>
					</>}
					{error && <p role='alert'>{error}</p>}
					<div className={styles.actions}>
						<AntButton onClick={close} disabled={saving}>Cancelar</AntButton>
						<AntButton onClick={load} disabled={saving}>Volver a cargar</AntButton>
						<AntButton type='primary' onClick={save} disabled={!snapshot || saving} loading={saving}>Guardar códigos</AntButton>
					</div>
				</>}
			</div>
		</Modal>
	</>;
}
